import { useMutation, useQueryClient } from '@tanstack/react-query'

import { client } from '../../services/client'
import { useToast } from '../../contexts/toastContext'
import { taskQueryKeys } from './queries'
import type { TaskDetail } from './queries'

type UpdateTaskInput = Partial<
  Pick<
    TaskDetail['followUp'],
    | 'isPublic'
    | 'title'
    | 'vendor'
    | 'trackingNumber'
    | 'note'
    | 'due'
    | 'trigger'
  >
> &
  Pick<TaskDetail['followUp'], 'objectId' | 'className'> & {
    completedAt?: boolean
  }

function useUpdateTask() {
  const queryClient = useQueryClient()
  const setToast = useToast()

  return useMutation(
    (task: UpdateTaskInput) =>
      client.post('functions/updateTask', {
        json: {
          ...task,
          isPublic: !task.isPublic, // opposite since update form uses "private" for the UI
          completedAt: task.completedAt
            ? {
                __type: 'Date',
                iso: new Date().toISOString(),
              }
            : { __op: 'Delete' },
          due: task.due
            ? {
                __type: 'Date',
                iso: task.due,
              }
            : null,
          objectId: task.objectId,
          className: task.className,
        },
      }),
    {
      onSettled: (_data, _error, variables) => {
        queryClient.invalidateQueries(
          taskQueryKeys.detail({
            id: variables.objectId,
            type: variables.className,
          })
        )
        queryClient.invalidateQueries(taskQueryKeys.lists())
      },
      onError: () => {
        setToast({
          message: 'Something went wrong while updating the Task',
          color: 'danger',
        })
      },
    }
  )
}

export type { UpdateTaskInput }
export { useUpdateTask }
